import type { StrategyFields } from '@/App/tiles/Option/strategy/types.ts';
import { computeHedgeDisplayRules } from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption } from '@/store/option/state.ts';

/** Field order is shared by labels, individual legs, summaries and shared hedge editors. */
export function getStrategyFields(option: FxOption, isInternalSales: boolean): StrategyFields {
  const { legs } = option.properties;
  const hedgeDisplay = computeHedgeDisplayRules(option);
  const isTriggerOrBinary = hasTriggerOrBinary(option);
  const showBarrier1Dates = isTriggerOrBinary && legs.some((leg) => leg.barrier1?.nature === 'WINDOW');
  const showBarrier2Dates = isTriggerOrBinary && legs.some((leg) => leg.barrier2?.nature === 'WINDOW');
  const hasSecondBarrier = legs.some((leg) => leg.barrier2 !== undefined);
  const hedgeFields = (['hedgeRate', 'hedgeAmount', 'hedgeDate'] as const).filter(
    (field) => hedgeDisplay[field] === 'strategy',
  );
  const fields: StrategyFields = [
    'side',
    ...(option.type !== 'Binary' ? ['type' as const] : []),
    'expiry',
    ...(option.type !== 'Binary' ? ['strike' as const] : []),
    'notional',
    'delivery',
    ...(isTriggerOrBinary ? ['triggerType' as const, 'firstTrigger1' as const, 'secondTrigger1' as const] : []),
    ...(showBarrier1Dates ? ['TriggerStartDate1' as const, 'TriggerEndDate1' as const] : []),
    ...(hasSecondBarrier ? ['firstTrigger2' as const, 'secondTrigger2' as const] : []),
    ...(showBarrier2Dates ? ['TriggerStartDate2' as const, 'TriggerEndDate2' as const] : []),
    [
      'premium',
      ...(hedgeDisplay.premiumDate === 'strategy' ? ['premiumDate' as const] : []),
      ...(isInternalSales ? ['markup' as const] : []),
      'volatility',
    ],
    hedgeFields,
    ...(isInternalSales ? ['atmVolatility' as const] : []),
    'forward',
    'swap',
    'zc1',
    'zc2',
    'delta',
    'vega',
    'gamma',
    'theta',
  ];

  return fields.filter((field) => !Array.isArray(field) || field.length > 0);
}

export function hasTriggerOrBinary(option: FxOption) {
  return (
    option.type === 'Trigger' ||
    option.type === 'Binary' ||
    option.properties.legs.some((leg) => leg.legProductName === 'Trigger' || leg.legProductName === 'Binary')
  );
}
